import React, { useEffect, useState } from 'react';
import { ask } from '../i18n';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Play, Pause, RotateCcw, XCircle, Download } from 'lucide-react';
import { api, useApi, when, secs, downloadFile } from '../api';
import { useShell } from '../Shell';
import { SkeletonPage, ErrorNote, statusPill, ShowMore, useIsPhone, usePaged } from '../ui';
import { cp } from '../paths';
import { usePrivacy } from '../privacy';
import { outcomeLabel } from './ai/AiCalls';

const STATS = [['total', 'Numbers'], ['completed', 'Called'], ['answered', 'Answered'], ['keyPressed', 'Pressed a key'], ['transferred', 'Transferred'], ['noAnswer', 'No answer']];
const AI_STATS = [['total', 'Numbers'], ['completed', 'Called'], ['answered', 'Talked'], ['booked', 'Booked'], ['interested', 'Interested'], ['callback', 'Call back'], ['skipped', 'Do-not-call']];
const LIVE = ['running', 'scheduled'];

function Waiting({ c }) {
  if (c.status !== 'running' || (!c.waitingForHours && !c.waitingForLine)) return null;
  return <div className="text-xs text-[#fbbf24] mt-3" data-testid="campaign-waiting-note">{c.waitingForLine ? 'All your lines are busy — waiting numbers are redialed automatically as soon as a line frees up.' : 'Waiting for calling hours — the remaining numbers are called between 8am and 9pm their local time.'}</div>;
}

function Controls({ c, act, busy }) {
  const btn = (action, label, Icon, cls = 'civr-btn-ghost') => <button className={`civr-btn ${cls} civr-btn-sm`} disabled={busy} onClick={() => act(action)} data-testid={`campaign-${action}-button`}><Icon className="w-4 h-4" />{label}</button>;
  return (
    <div className="flex flex-wrap gap-2">
      {c.status === 'created' && btn('start', 'Start calling', Play, 'civr-btn-primary')}
      {c.status === 'running' && btn('pause', 'Pause', Pause)}
      {c.status === 'paused' && btn('resume', 'Resume', RotateCcw, 'civr-btn-primary')}
      {['created', 'running', 'paused', 'scheduled'].includes(c.status) && btn('cancel', 'Cancel', XCircle, 'civr-btn-danger')}
    </div>
  );
}

function LeadStatus({ l, i }) {
  return (
    <>
      <span className={statusPill(l.status)}>{l.status}</span>
      {l.transferred && <span className="civr-pill civr-pill-on ml-1">transferred</span>}
      {l.waitingForLine && l.status === 'pending' && <span className="civr-pill civr-pill-warn ml-1" data-testid={`campaign-lead-${i}-waiting-line`}>waiting for a line</span>}
    </>
  );
}

function LeadResult({ l, i, isAi }) {
  const pv = usePrivacy();
  if (!isAi) return l.digitPressed || '—';
  if (!l.ai) return l.hangupCause && l.status === 'skipped' ? l.hangupCause : '—';
  return (
    <>
      <span className="civr-pill">{outcomeLabel(l.ai.outcome)}</span>
      {l.ai.booking && <div className="text-[#e4e4e7] mt-1" data-testid={`campaign-lead-${i}-booking`}>{`Booked ${l.ai.booking.day} at ${l.ai.booking.time}`}</div>}
      {l.ai.summary && <div className="text-[#a1a1aa] mt-1">{pv.text(l.ai.summary, 'Summary hidden')}</div>}
    </>
  );
}

// Phones: one stacked card per number.
function LeadCard({ l, i, isAi }) {
  const pv = usePrivacy();
  return (
    <li className="py-3 first:pt-0 last:pb-0" data-testid={`campaign-lead-${i}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0"><div className="font-mono text-sm break-all">{pv.phone(l.number)}</div>{l.name && <div className="text-xs text-[#a1a1aa] mt-0.5">{pv.text(l.name, '•••')}</div>}</div>
        {l.duration ? <span className="font-mono text-xs text-[#a1a1aa] shrink-0 pt-0.5">{secs(l.duration)}</span> : null}
      </div>
      <div className="flex flex-wrap items-center gap-y-1 mt-2"><LeadStatus l={l} i={i} /></div>
      <div className="text-xs mt-2"><span className="text-[#a1a1aa] mr-2">{isAi ? 'AI result' : 'Key'}</span><span className={isAi ? '' : 'font-mono'} data-testid={`campaign-lead-${i}-result`}><LeadResult l={l} i={i} isAi={isAi} /></span></div>
    </li>
  );
}

function Leads({ leads, isAi }) {
  const pv = usePrivacy();
  const phone = useIsPhone();
  const { shown, rest, more } = usePaged(leads, 50);
  return (
    <>
      {phone ? <ul className="divide-y divide-[#1f1f1f]" data-testid="campaign-leads-table">{shown.map((l, i) => <LeadCard key={`${l.number}-${i}`} l={l} i={i} isAi={isAi} />)}</ul> : (
        <table className="civr-table" data-testid="campaign-leads-table">
          <thead><tr><th>Number</th><th>Name</th><th>Status</th><th>{isAi ? 'AI result' : 'Key'}</th><th>Length</th></tr></thead>
          <tbody>{shown.map((l, i) => (
            <tr key={`${l.number}-${i}`} data-testid={`campaign-lead-${i}`}><td className="font-mono text-xs">{pv.phone(l.number)}</td><td>{pv.text(l.name, '•••') || '—'}</td><td><LeadStatus l={l} i={i} /></td><td className={isAi ? 'text-xs max-w-xs' : 'font-mono'} data-testid={`campaign-lead-${i}-result`}><LeadResult l={l} i={i} isAi={isAi} /></td><td className="font-mono text-xs">{l.duration ? secs(l.duration) : '—'}</td></tr>
          ))}</tbody>
        </table>
      )}
      <ShowMore rest={rest} onClick={more} testid="campaign-leads-show-more" />
    </>
  );
}


export default function CampaignDetail() {
  const { id } = useParams();
  const { refreshSummary } = useShell();
  const pv = usePrivacy();
  const { data, error, loading, reload, setData } = useApi(`/campaigns/${id}`);
  const [busy, setBusy] = useState(false);
  const c = data?.campaign;
  useEffect(() => { if (!c || !LIVE.includes(c.status)) return undefined; const t = setInterval(reload, 5000); return () => clearInterval(t); }, [c, reload]);

  const act = async (action) => {
    if (action === 'cancel' && !(await ask('Cancel this campaign? No more numbers are dialed. Calls still ringing or playing the message end now; calls already transferred to you keep going.', { danger: true, ok: 'Cancel campaign', cancel: 'Keep it running' }))) return;
    setBusy(true);
    try {
      const d = await api(`/campaigns/${id}/${action}`, { method: 'POST' });
      if (d.dryRun) toast.message(d.message); else toast.success(`Campaign ${action === 'start' ? 'started' : action === 'cancel' ? 'cancelled' : `${action}d`}`);
      if (d.campaign) setData(p => ({ campaign: { ...p.campaign, ...d.campaign } })); else reload();
      refreshSummary();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const back = <Link to={cp('/app/campaigns')} className="inline-flex items-center gap-2 min-h-[44px] text-sm text-[#a1a1aa] hover:text-[#818cf8] mb-3" data-testid="campaign-back-link"><ArrowLeft className="w-4 h-4" />All campaigns</Link>;
  if (loading && !c) return <div>{back}<SkeletonPage cards={0} rows={4} /></div>;
  if (error && !c) return <div data-testid="campaign-detail-page">{back}<ErrorNote error={error} onRetry={reload} /></div>;
  const isAi = c.mode === 'ai';
  const pct = c.stats?.total ? Math.round(((c.stats.completed || 0) / c.stats.total) * 100) : 0;
  return (
    <div data-testid="campaign-detail-page">
      {back}
      <div className="flex flex-col md:flex-row md:items-end gap-4 justify-between mb-8">
        <div>
          <div className="civr-label">{isAi ? `AI agent conversation · ${c.agentName || ''}` : `${c.mode === 'menu' ? 'Menu' : c.mode === 'transfer' ? `Transfer to ${pv.phone(c.transferNumber)}` : 'Report key presses'} · keys ${c.activeKeys?.join(' ')}`}</div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold mt-2" data-testid="campaign-title">{c.audioName || 'Campaign'}</h1>
          <div className="text-sm text-[#a1a1aa] font-mono mt-1">from {pv.phone(c.callerId)} · created {when(c.createdAt)}{c.scheduledFor ? ` · scheduled ${when(c.scheduledFor)}` : ''}</div>
        </div>
        <div className="flex items-center gap-2"><span className={statusPill(c.status)} data-testid="campaign-status">{c.status}</span>{c.dryRun && <span className="civr-pill civr-pill-warn">preview</span>}</div>
      </div>
      <div className="civr-card p-6 mb-4">
        <div className="flex items-center justify-between mb-3"><span className="text-sm text-[#d4d4d8]">Progress</span><span className="font-mono text-sm" data-testid="campaign-progress">{pct}%</span></div>
        <div className="h-2 rounded-full bg-[#262626] overflow-hidden"><div className={`h-full bg-[#818cf8] transition-[width] duration-700 ${c.status === 'running' ? 'civr-pulse' : ''}`} style={{ width: `${pct}%` }} /></div>
        <div className={`grid gap-4 mt-6 ${isAi ? 'grid-cols-4 md:grid-cols-7' : 'grid-cols-3 md:grid-cols-6'}`}>{(isAi ? AI_STATS : STATS).map(([k, l]) => <div key={k} data-testid={`campaign-stat-${k}`}><div className="font-mono text-2xl">{c.stats?.[k] || 0}</div><div className="text-xs text-[#a1a1aa]">{l}</div></div>)}</div>
        <div className="mt-6 flex flex-wrap gap-2"><Controls c={c} act={act} busy={busy} />
          <button className="civr-btn civr-btn-ghost civr-btn-sm" onClick={() => downloadFile(`/campaigns/${id}/report.csv`, `campaign-${String(id).slice(0, 8)}.csv`).catch(e => toast.error(e.message))} data-testid="campaign-report-download-button"><Download className="w-4 h-4" />Download CSV report</button></div>
        <Waiting c={c} />
        {(c.cancelledReason || c.pausedReason) && <div className="text-xs text-[#a1a1aa] mt-3" data-testid="campaign-reason">{c.cancelledReason || c.pausedReason}</div>}
      </div>
      <div className="civr-card p-5 sm:p-6 overflow-x-auto">
        <div className="font-display text-2xl font-bold mb-3">Numbers</div>
        <Leads leads={c.leads} isAi={isAi} />
      </div>
    </div>
  );
}
